
import { createContext, useContext, useEffect, FC, ReactNode, useRef } from 'react';
import { AppState } from '../types';
import { useQuestState } from '../hooks/useQuestState';
import { useDailyState } from '../hooks/useDailyState';
import { useSettingsState } from '../hooks/useSettingsState';
import { useAppState, getInitialState } from '../hooks/useAppState';
import { CATEGORY_ORDER } from '../constants';
import isEqual from 'lodash.isequal';

type AppContextType = {
  quests: ReturnType<typeof useQuestState>;
  daily: ReturnType<typeof useDailyState>;
  settings: ReturnType<typeof useSettingsState>;
  isLoaded: boolean;
  version: string;
  loadInitialState: () => Promise<void>;
  setAppAsLoaded: () => void;
};

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { appState, setAppState, isLoaded, loadInitialState, setAppAsLoaded } = useAppState();
  const previousStateRef = useRef<AppState | null>(null);
  
  const quests = useQuestState(appState || getInitialState());
  const daily = useDailyState(appState || getInitialState());
  const settings = useSettingsState(appState || getInitialState());

  useEffect(() => {
    if (appState) {
      quests.setQuests(appState.quests);
      quests.setRejectedQuests(appState.rejectedQuests);
      quests.setActiveQuests(appState.activeQuests);
      quests.setOfferedQuests(appState.offeredQuests);
      quests.setCompletedQuestIds(appState.completedQuestIds);
      quests.setLastDrawnQuestIds(appState.lastDrawnQuestIds);
      daily.setTokens(appState.tokens);
      daily.setHistory(appState.history);
      daily.setQuestTokenEarnedToday(appState.questTokenEarnedToday);
      daily.setDailyChecklistCompleted(appState.dailyChecklistCompleted);
      settings.setTheme(appState.theme);
      settings.setTokenIcon(appState.tokenIcon);
      settings.setBackgroundImage(appState.backgroundImage);
      settings.setBackgroundImageSizing(appState.backgroundImageSizing);
    }
  }, [appState]);

  const getCurrentState = (): AppState | null => {
    if (!appState) return null;
    
    return {
      version: appState.version,
      date: appState.date,
      quests: quests.quests,
      rejectedQuests: quests.rejectedQuests,
      activeQuests: quests.activeQuests,
      offeredQuests: quests.offeredQuests,
      completedQuestIds: quests.completedQuestIds,
      lastDrawnQuestIds: quests.lastDrawnQuestIds,
      tokens: daily.tokens,
      history: daily.history,
      questTokenEarnedToday: daily.questTokenEarnedToday,
      dailyChecklistCompleted: daily.dailyChecklistCompleted,
      theme: settings.theme,
      tokenIcon: settings.tokenIcon,
      backgroundImage: settings.backgroundImage,
      backgroundImageSizing: settings.backgroundImageSizing,
    };
  }

  useEffect(() => {
    const currentState = getCurrentState();
  
    if (isLoaded && currentState && !isEqual(currentState, previousStateRef.current)) {
      setAppState(currentState);
      previousStateRef.current = currentState;
    }
  }, [quests, daily, settings, isLoaded, appState, setAppState]);
  
  useEffect(() => {
    if (isLoaded) {
      CATEGORY_ORDER.forEach(category => {
        if (quests.offeredQuests && !quests.offeredQuests[category]) {
          quests.drawQuestForCategory(category);
        }
      });
    }
  }, [isLoaded, quests.drawQuestForCategory, quests.offeredQuests]);

  const value = { 
    quests, 
    daily, 
    settings, 
    isLoaded,
    version: appState?.version || '',
    loadInitialState,
    setAppAsLoaded,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
